import * as React from "react"
import { CheckCircle2, XCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { csrfToken } from "@/lib/csrf"

export type ConnectionResult = {
  ok: boolean
  message: string
  details: { label: string; value: string }[]
  checked_at: string
}

export type ConnectionDetail = { label: string; value: string }

// A label column that sizes to its widest label instead of being fixed.
// Several labels here are SQL Server view names -- domo.v_sms_automation_
// member_roster is 35 characters -- and a fixed 10rem column let those run
// straight over the value beside them.
const DETAIL_LIST = "m-0 grid gap-x-4 gap-y-1 text-sm sm:grid-cols-[minmax(0,max-content)_1fr]"
const DETAIL_LABEL = "min-w-0 break-words text-ink-muted"
const DETAIL_VALUE = "m-0 min-w-0 break-words text-ink-body"

/**
 * One external service, with a button that really exercises it.
 *
 * Reached by raw `fetch()` rather than Inertia's router, so pressing Test does
 * not navigate away from a half-typed number — the same carve-out
 * TestQueryPanel uses, and the one AGENTS.md allows for JSON endpoints.
 */
export function ConnectionPanel({
  title,
  description,
  path,
  details,
  configured,
  notConfiguredMessage,
  target,
  warning,
}: {
  title: string
  description: string
  path: string
  details: ConnectionDetail[]
  configured: boolean
  notConfiguredMessage: string
  /** Present when the test needs somewhere to send to. */
  target?: {
    label: string
    placeholder: string
    type?: string
    help: string
    buttonLabel: string
  }
  /** Shown above the button when pressing it does something surprising. */
  warning?: React.ReactNode
}) {
  const [value, setValue] = React.useState("")
  const [running, setRunning] = React.useState(false)
  const [result, setResult] = React.useState<ConnectionResult | null>(null)

  const inputId = `${path.replace(/\W+/g, "_")}_target`
  const canRun = !running && (!target || value.trim().length > 0)

  const run = async () => {
    setRunning(true)
    try {
      const response = await fetch(path, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify(target ? { to: value.trim() } : {}),
      })
      setResult(await response.json())
    } catch (error) {
      // A network-level failure never reaches the server, so it has no server
      // sentence — say what happened rather than printing the raw error alone.
      setResult({
        ok: false,
        message: `The test could not be started: ${String(error)}. The Portal itself may be restarting.`,
        details: [],
        checked_at: new Date().toISOString(),
      })
    } finally {
      setRunning(false)
    }
  }

  return (
    <section className="callout space-y-4">
      <div>
        <h2 className="text-base">{title}</h2>
        <p className="mt-1 text-sm text-ink-muted">{description}</p>
      </div>

      {details.length > 0 && (
        <dl className={DETAIL_LIST}>
          {details.map((detail) => (
            <React.Fragment key={detail.label}>
              <dt className={DETAIL_LABEL}>{detail.label}</dt>
              <dd className={DETAIL_VALUE}>{detail.value || "—"}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}

      {!configured && (
        <p className="m-0 text-sm text-ink-muted">{notConfiguredMessage}</p>
      )}

      {warning}

      <div className="flex flex-wrap items-end gap-3">
        {target && (
          <div className="min-w-0 flex-1 sm:max-w-xs">
            <label htmlFor={inputId}>{target.label}</label>
            <Input
              id={inputId}
              type={target.type ?? "text"}
              className="mt-1"
              value={value}
              placeholder={target.placeholder}
              onChange={(event) => setValue(event.target.value)}
            />
            <p className="mt-1 text-xs text-ink-muted">{target.help}</p>
          </div>
        )}

        <Button onClick={run} disabled={!canRun}>
          {running ? "Testing…" : target ? target.buttonLabel : "Test connection"}
        </Button>
      </div>

      {result && <Outcome result={result} />}
    </section>
  )
}

function Outcome({ result }: { result: ConnectionResult }) {
  const Icon = result.ok ? CheckCircle2 : XCircle

  return (
    <div
      role="status"
      className={`rounded-md border p-3 ${
        result.ok
          ? "border-hairline bg-surface"
          : "border-danger/40 bg-danger/5"
      }`}
    >
      <div className="flex items-start gap-2">
        <Icon
          className={`mt-0.5 h-4 w-4 shrink-0 ${result.ok ? "text-accent" : "text-danger"}`}
          aria-hidden="true"
        />
        <div className="min-w-0">
          <p className="m-0 text-sm text-ink-body">{result.message}</p>

          {result.details.length > 0 && (
            <dl className={`${DETAIL_LIST} mt-2 text-xs`}>
              {result.details.map((detail) => (
                <React.Fragment key={detail.label}>
                  <dt className={DETAIL_LABEL}>{detail.label}</dt>
                  <dd className={DETAIL_VALUE}>{detail.value || "—"}</dd>
                </React.Fragment>
              ))}
            </dl>
          )}
        </div>
      </div>
    </div>
  )
}
